import type React from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Box, Button, CircularProgress, Grid, Paper, Typography, useTheme } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { tokens } from "../../theme";
import IconButton from "../../components/ui/IconButton";
import { deleteApplicationVideo, getSubmissions } from "./services/jobsStorage";
import type { ApplicationSubmission } from "./types";

const formatFileSize = (bytes?: number | null) => {
  if (!bytes) return null;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const VideosPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [submissions, setSubmissions] = useState<ApplicationSubmission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadData = async () => {
    setLoadError(null);
    try {
      setSubmissions(await getSubmissions());
    } catch {
      setLoadError("Unable to load video submissions. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, []);

  const videoSubmissions = submissions.filter((sub) => sub.videoPitch?.videoUrl);

  const handleDelete = async (sub: ApplicationSubmission) => {
    if (!window.confirm(`Delete ${sub.candidateInfo.fullName}'s video pitch? This cannot be undone.`)) {
      return;
    }
    setDeletingId(sub.id);
    try {
      await deleteApplicationVideo(sub.id);
      setSubmissions((prev) =>
        prev.map((s) => (s.id === sub.id ? { ...s, videoPitch: undefined } : s)),
      );
    } catch {
      setLoadError("Unable to delete the video. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Box>
      <Button
        onClick={() => navigate(-1)}
        startIcon={<ArrowBackIosNewIcon sx={{ fontSize: 14 }} />}
        sx={{
          minWidth: "auto",
          px: 2,
          py: 1.1,
          mb: 2,
          borderRadius: "999px",
          bgcolor: colors.primary[800],
          color: colors.primary[400],
          textTransform: "none",
          fontWeight: 600,
          boxShadow: "none",
          border: `1px solid ${colors.primary[400]}`,
          "&:hover": { borderColor: `${colors.primary[400]}44` },
        }}
      >
        BACK
      </Button>

      {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}

      <Paper
        elevation={0}
        sx={{
          borderRadius: "16px",
          boxShadow: "none",
          p: 2.5,
          backgroundColor: colors.primary[900],
        }}
      >
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : videoSubmissions.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography variant="body2" sx={{ color: colors.primary[700] }}>
              No video pitches have been submitted yet.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {videoSubmissions.map((sub) => (
              <Grid key={sub.id} size={{ xs: 12, sm: 6, md: 4 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.25,
                    borderRadius: "12px",
                    backgroundColor: colors.primary[800],
                    p: 2,
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      borderRadius: "10px",
                      overflow: "hidden",
                      backgroundColor: "#0b0b0b",
                      aspectRatio: "16 / 9",
                    }}
                  >
                    <video
                      src={sub.videoPitch?.videoUrl}
                      controls
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                    <IconButton
                      size="small"
                      tooltip="Delete video"
                      disabled={deletingId === sub.id}
                      onClick={() => handleDelete(sub)}
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        backgroundColor: "rgba(255,255,255,0.92)",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                        "&:hover": { backgroundColor: "#fff" },
                      }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 18, color: theme.palette.error.main }} />
                    </IconButton>
                  </Box>

                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={700} noWrap sx={{ color: colors.primary[100] }}>
                        {sub.candidateInfo.fullName}
                      </Typography>
                      <Typography variant="caption" sx={{ color: colors.primary[700], display: "block" }} noWrap>
                        {sub.jobTitle}
                      </Typography>
                    </Box>
                  </Box>

                  <Typography variant="caption" sx={{ color: colors.primary[700] }}>
                    {sub.videoPitch?.durationSeconds}s
                    {formatFileSize(sub.videoPitch?.sizeBytes) && ` • ${formatFileSize(sub.videoPitch?.sizeBytes)}`}
                    {" • Submitted "}
                    {new Date(sub.submittedAt).toLocaleDateString()}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>
    </Box>
  );
};

export default VideosPage;
